import React, { createContext, useContext } from 'react';
import { useSearchParams } from 'react-router';

import { combineUniqueUrlSearchParams } from 'context/SearchParamsProvider/utils/combineUniqueUrlSearchParams';
import { Filters } from 'features/Filters/types/Filters';
import {
  toFilters,
  toFiltersSearchParams,
  toSearchTerm,
  toSearchTermSearchParams,
} from 'utils/searchParamsConverter';

const SearchParamsContext = createContext<{
  searchParams: URLSearchParams;
  updateFiltersSearchParams: (newFilters: Filters) => void;
}>({
  searchParams: new URLSearchParams(),
  updateFiltersSearchParams: () => {},
});

export const useSearchParamsProvider = () => useContext(SearchParamsContext);

type SearchParamsProviderProps = {
  children: React.ReactNode;
};

const getFiltersSearchParams = (currentParams: URLSearchParams, newFilters?: Filters) => {
  const existingFilters = toFilters(currentParams);
  if (newFilters) {
    return toFiltersSearchParams({ ...existingFilters, ...newFilters });
  } else {
    return toFiltersSearchParams(existingFilters);
  }
};

const getSearchTermSearchParams = (currentParams: URLSearchParams) => {
  const existingSearchTerm = toSearchTerm(currentParams);
  if (existingSearchTerm) {
    return toSearchTermSearchParams(existingSearchTerm);
  }
  return new URLSearchParams();
};

export const SearchParamsProvider = ({ children }: SearchParamsProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = (newFilters?: Filters) => {
    const currentParams = new URLSearchParams(searchParams.toString());
    const filtersQueryParam = getFiltersSearchParams(currentParams, newFilters);
    const searchTermParam = getSearchTermSearchParams(currentParams);
    const urlSearchParams = combineUniqueUrlSearchParams(filtersQueryParam, searchTermParam);
    setSearchParams(urlSearchParams);
  };

  const updateFiltersSearchParams = (newFilters: Filters) => {
    updateSearchParams(newFilters);
  };

  return (
    <SearchParamsContext.Provider value={{ searchParams, updateFiltersSearchParams }}>
      {children}
    </SearchParamsContext.Provider>
  );
};
